import React, { useState, useEffect, useCallback, useRef } from 'react'

import { XStack, Separator } from 'tamagui'

import { Text } from '../Text'
import { Icon } from '../Icon'
import { SearchGroupItem, useSearchGroup } from './SearchGroup'

type SuggestedAction = {
  id: string
  label: string
  icon:
    | 'people-community'
    | 'calendar'
    | 'projection-screen'
    | 'globe-location'
    | 'heart'
    | 'search'
  type: 'navigate' | 'search'
}

const CREATE_ACTIONS: SuggestedAction[] = [
  {
    id: 'create-event',
    label: 'Create an Event',
    icon: 'calendar',
    type: 'navigate',
  },
  {
    id: 'create-presentation',
    label: 'Create a Presentation',
    icon: 'projection-screen',
    type: 'navigate',
  },
  {
    id: 'create-guild',
    label: 'Create a Guild',
    icon: 'people-community',
    type: 'navigate',
  },
]

// --- Typewriter animation config ---

const TYPEWRITER_ROWS = [
  {
    prefix: 'Events',
    icon: 'globe-location' as const,
    suffixes: [
      ' near me',
      ' about AI',
      ' this week',
      ' happening tomorrow',
      ' about React',
      ' about React Native',
      ' about JavaScript',
      ' about web performance',
      ' happening today',
      ' about mobile development',
      ' about civic tech',
      ' about Node.js',
      ' about GraphQL',
      ' in my city',
      ' about testing',
      ' about career growth',
      ' about full-stack development',
      ' about CSS',
      ' about Rust',
      ' about Svelte',
      ' about databases',
      ' about cloud computing',
      ' about machine learning',
      ' about micro-frontends',
      ' about animations',
      ' about design systems',
      ' in London',
      ' in Toronto',
      ' in Amsterdam',
      ' in São Paulo',
      ' in Montréal',
      ' in Prague',
      ' in Berlin',
      ' in New York',
      ' about React in Amsterdam',
      ' about React in Berlin',
      ' about JavaScript in Toronto',
      ' about React Native in London',
      ' about GraphQL in London',
      ' about Node.js in Brazil',
    ],
  },
  {
    prefix: 'Presentations',
    icon: 'heart' as const,
    suffixes: [
      ' about React',
      ' about AI',
      ' about web performance',
      ' about GraphQL',
      ' about React Native',
      ' on testing strategies',
      ' about full-stack development',
      ' on debugging techniques',
      ' about JavaScript',
      ' about Node.js',
      ' about mobile development',
      ' about civic tech',
      ' about career growth',
      ' about CSS',
      ' about Rust',
      ' about Svelte',
      ' about databases',
      ' about cloud computing',
      ' about machine learning',
      ' about micro-frontends',
      ' about animations',
      ' about design systems',
    ],
  },
  {
    prefix: 'Guilds',
    icon: 'people-community' as const,
    suffixes: [
      ' in my area',
      ' for React developers',
      ' for React Native developers',
      ' about AI',
      ' near me',
      ' in London',
      ' in Toronto',
      ' in Amsterdam',
      ' in São Paulo',
      ' in Montréal',
      ' in Europe',
      ' in Berlin',
      ' in New York',
      ' about JavaScript',
      ' for mobile developers',
      ' about civic tech',
      ' for remote workers',
      ' in my city',
      ' about Node.js',
      ' about GraphQL',
      ' about CSS',
      ' about Rust',
      ' about Svelte',
      ' about databases',
      ' about cloud computing',
      ' about machine learning',
      ' about design systems',
      ' for React developers in Amsterdam',
      ' about Node.js in Brazil',
      ' for React Native developers in London',
      ' about GraphQL in Austin',
    ],
  },
]

const TYPING_SPEED = 60
const ERASING_SPEED = 40
const PAUSE_AFTER_TYPING = 2000
const PAUSE_AFTER_ERASING = 300

// Each row types ONE suffix, then hands off to the next row.
// When it's this row's turn again, it erases the old suffix and types the next one.
//
// Uses a single setInterval loop to avoid stale-closure and effect-cleanup bugs
// that occur with chained setTimeout + setState patterns.

const useTypewriterRow = (
  suffixes: string[],
  isActive: boolean,
  isPaused: boolean,
  onComplete: () => void
) => {
  const [displayedSuffix, setDisplayedSuffix] = useState('')
  const [showCursor, setShowCursor] = useState(false)

  const stateRef = useRef({
    suffixIndex: Math.floor(Math.random() * suffixes.length),
    charIndex: 0,
    phase: 'idle' as 'idle' | 'typing' | 'paused' | 'erasing' | 'done',
    pauseUntil: 0,
    isFirstTurn: true,
  })

  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const isPausedRef = useRef(isPaused)
  isPausedRef.current = isPaused

  // Drive the animation with a single interval when active
  useEffect(() => {
    if (!isActive) {
      setShowCursor(false)
      return
    }

    setShowCursor(true)
    const s = stateRef.current

    if (s.isFirstTurn) {
      s.isFirstTurn = false
      s.charIndex = 0
      s.phase = 'typing'
    } else {
      s.phase = 'erasing'
      s.pauseUntil = Date.now() + PAUSE_AFTER_ERASING
    }

    const tick = () => {
      // Skip ticks while hover-paused
      if (isPausedRef.current) return

      const now = Date.now()
      if (s.pauseUntil > now) return

      const target = suffixes[s.suffixIndex]

      switch (s.phase) {
        case 'typing':
          if (s.charIndex < target.length) {
            s.charIndex++
            setDisplayedSuffix(target.slice(0, s.charIndex))
          } else {
            s.phase = 'paused'
            s.pauseUntil = now + PAUSE_AFTER_TYPING
          }
          break

        case 'paused':
          // Pause is over — hand off to next row
          s.phase = 'done'
          setShowCursor(false)
          onCompleteRef.current()
          break

        case 'erasing':
          if (s.charIndex > 0) {
            s.charIndex--
            setDisplayedSuffix(target.slice(0, s.charIndex))
          } else {
            // Done erasing — pick a random different suffix, start typing after brief pause
            let next = Math.floor(Math.random() * (suffixes.length - 1))
            if (next >= s.suffixIndex) next++
            s.suffixIndex = next
            s.phase = 'typing'
            s.pauseUntil = now + PAUSE_AFTER_ERASING
          }
          break
      }
    }

    const interval = setInterval(tick, ERASING_SPEED)
    // Fire first tick for typing at typing speed
    const firstTick = setTimeout(
      tick,
      s.phase === 'typing' ? TYPING_SPEED : ERASING_SPEED
    )

    return () => {
      clearInterval(interval)
      clearTimeout(firstTick)
    }
  }, [isActive, suffixes])

  // Blinking cursor
  const [cursorVisible, setCursorVisible] = useState(true)
  useEffect(() => {
    if (!showCursor) return
    setCursorVisible(true)
    const timer = setInterval(() => setCursorVisible((v) => !v), 530)
    return () => clearInterval(timer)
  }, [showCursor])

  return {
    text: displayedSuffix,
    cursor: showCursor && cursorVisible ? '|' : showCursor ? '\u00A0' : '',
  }
}

const TypewriterRow = ({
  row,
  index,
  isActive,
  onComplete,
  onSelectAction,
}: {
  row: (typeof TYPEWRITER_ROWS)[number]
  index: number
  isActive: boolean
  onComplete: () => void
  onSelectAction: (action: SuggestedAction) => void
}) => {
  const stableOnComplete = useCallback(onComplete, [onComplete])
  const { activeIndex } = useSearchGroup()
  const groupIndex = CREATE_ACTIONS.length + index
  const isHovered = activeIndex === groupIndex

  const { text, cursor } = useTypewriterRow(
    row.suffixes,
    isActive,
    isHovered,
    stableOnComplete
  )

  const textRef = useRef(text)
  textRef.current = text

  return (
    <SearchGroupItem
      key={`search-${index}`}
      index={groupIndex}
      onSelect={() =>
        onSelectAction({
          id: `search-${index}`,
          label: row.prefix + textRef.current,
          icon: row.icon,
          type: 'search',
        })
      }
    >
      <XStack gap='$3' alignItems='center'>
        <Icon name={row.icon} size='$5' color='$color' />
        <Text fontSize='$5'>
          {row.prefix}
          <Text fontSize='$5' color='$colorSubtle'>
            {text}
          </Text>
          <Text fontSize='$5' color='$colorSubtle' style={{ opacity: 0.6 }}>
            {cursor}
          </Text>
        </Text>
      </XStack>
    </SearchGroupItem>
  )
}

export const SearchSuggestedActions = ({
  onSelectAction,
}: {
  onSelectAction: (action: SuggestedAction) => void
}) => {
  const [activeRow, setActiveRow] = useState(0)

  const handleRowComplete = useCallback((rowIndex: number) => {
    setActiveRow((rowIndex + 1) % TYPEWRITER_ROWS.length)
  }, [])

  return (
    <>
      {CREATE_ACTIONS.map((action, index) => (
        <SearchGroupItem
          key={action.id}
          index={index}
          onSelect={() => onSelectAction(action)}
        >
          <XStack gap='$3' alignItems='center'>
            <Icon name={action.icon} size='$5' color='$color' />
            <Text fontSize='$5'>{action.label}</Text>
          </XStack>
        </SearchGroupItem>
      ))}

      <Separator
        marginVertical='$2'
        marginHorizontal='$3'
        borderColor='$colorSubtle'
        style={{ opacity: 0.5 }}
      />

      {TYPEWRITER_ROWS.map((row, index) => (
        <TypewriterRow
          key={row.prefix}
          row={row}
          index={index}
          isActive={activeRow === index}
          onComplete={() => handleRowComplete(index)}
          onSelectAction={onSelectAction}
        />
      ))}
    </>
  )
}

export { type SuggestedAction }
export const TOTAL_SUGGESTED_ACTIONS =
  CREATE_ACTIONS.length + TYPEWRITER_ROWS.length
